import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const eventTypes = [
  "Private Dinner",
  "Intimate Celebration",
  "Corporate & Brand Hosting",
  "Luxury Wedding Event",
  "Bespoke Gathering",
  "Product Launch Dinner",
  "Other",
];

const budgetRanges = [
  "£5,000 – £10,000",
  "£10,000 – £25,000",
  "£25,000 – £50,000",
  "£50,000 – £100,000",
  "£100,000+",
  "Prefer not to disclose",
];

type SubmitStatus = "idle" | "submitting" | "success" | "error";

export default function InquiryPage() {
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setStatus("submitting");

    const form = e.currentTarget;
    const data = new URLSearchParams();
    const fields = form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      "input, select, textarea"
    );
    fields.forEach((field) => {
      if (field.name) data.append(field.name, field.value);
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/d7ducqpnoju8nk5a1go0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <main className="min-h-screen bg-[#FAF8F5]">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[50vh] min-h-[380px] flex items-end overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/c67bcce61f028bc75ef33d98bc03b67f.jpg"
            alt="Table Privé Inquiry — Bespoke private dining experience"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/30 to-black/75" />
        </div>
        <div className="relative z-10 w-full px-8 md:px-20 pb-14 md:pb-20">
          <div className="flex items-center gap-4 mb-5">
            <div className="h-px w-10 bg-[#C9A961]" />
            <span className="text-[#C9A961] tracking-[0.25em] uppercase text-xs font-light">
              Exclusive Access
            </span>
          </div>
          <h1 className="font-serif text-white text-5xl md:text-7xl font-light tracking-tight leading-none">
            Request Your Experience
          </h1>
        </div>
      </section>

      {/* Intro */}
      <section className="pt-16 pb-0 px-8 md:px-20">
        <div className="max-w-2xl">
          <p className="font-serif text-[#2C2420] text-xl md:text-2xl leading-relaxed font-light tracking-wide">
            Every Table Privé experience is privately commissioned. Share the details of your vision, and we will curate a proposal defined by atmosphere, precision, and discretion — entirely tailored to you.
          </p>
          <div className="mt-8 flex items-center gap-4">
            <div className="h-px w-10 bg-[#C9A961]/70" />
            <span className="text-[#8A7F74] tracking-[0.18em] uppercase text-xs font-light">
              Discretion is guaranteed
            </span>
          </div>
          <div className="mt-10 pt-10 border-t border-[#E8E2D9]">
            <p className="text-[#8A7F74] text-[10px] tracking-[0.22em] uppercase font-light mb-2">
              For private inquiries and collaborations
            </p>
            <a
              href="mailto:hello@tableprive.com"
              className="font-serif text-[#2C2420] text-lg md:text-xl font-light tracking-wide hover:text-[#C9A961] transition-colors duration-300 cursor-pointer"
            >
              hello@tableprive.com
            </a>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 md:py-24 px-8 md:px-20">
        <div className="max-w-3xl">

          {status === "success" ? (
            <div className="py-20 text-center border border-[#C9A961]/30 bg-[#F5F1EC]">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="h-px w-10 bg-[#C9A961]" />
                <span className="text-[#C9A961] tracking-[0.2em] uppercase text-xs font-light">Received</span>
                <div className="h-px w-10 bg-[#C9A961]" />
              </div>
              <h2 className="font-serif text-[#1A1A1A] text-3xl font-light mb-4">
                Your inquiry has been received.
              </h2>
              <p className="text-[#8A7F74] font-light text-sm tracking-wide max-w-sm mx-auto leading-relaxed">
                Our team will review your vision and be in touch shortly with a tailored response. We look forward to creating something exceptional with you.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-3 mt-10 px-8 py-3 border border-[#1A1A1A] text-[#1A1A1A] text-xs tracking-[0.18em] uppercase font-light hover:bg-[#1A1A1A] hover:text-[#FAF8F5] transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Return Home
              </Link>
            </div>
          ) : (
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-0"
            >
              {/* Row 1: Name + Email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#DDD8D0]">
                <div className="bg-[#FAF8F5] p-6 md:p-8">
                  <label className="block text-[#8A7F74] text-[10px] tracking-[0.25em] uppercase font-light mb-3">
                    Full Name <span className="text-[#C9A961]">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Your full name"
                    className="w-full bg-transparent border-b border-[#D8D0C4] focus:border-[#C9A961] outline-none text-[#1A1A1A] font-light text-sm tracking-wide pb-2 transition-colors placeholder-[#C8C0B5]"
                  />
                </div>
                <div className="bg-[#FAF8F5] p-6 md:p-8">
                  <label className="block text-[#8A7F74] text-[10px] tracking-[0.25em] uppercase font-light mb-3">
                    Email Address <span className="text-[#C9A961]">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    className="w-full bg-transparent border-b border-[#D8D0C4] focus:border-[#C9A961] outline-none text-[#1A1A1A] font-light text-sm tracking-wide pb-2 transition-colors placeholder-[#C8C0B5]"
                  />
                </div>
              </div>

              {/* Row 2: Phone + Event Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#DDD8D0] mt-px">
                <div className="bg-[#FAF8F5] p-6 md:p-8">
                  <label className="block text-[#8A7F74] text-[10px] tracking-[0.25em] uppercase font-light mb-3">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="+44 000 000 0000"
                    className="w-full bg-transparent border-b border-[#D8D0C4] focus:border-[#C9A961] outline-none text-[#1A1A1A] font-light text-sm tracking-wide pb-2 transition-colors placeholder-[#C8C0B5]"
                  />
                </div>
                <div className="bg-[#FAF8F5] p-6 md:p-8">
                  <label className="block text-[#8A7F74] text-[10px] tracking-[0.25em] uppercase font-light mb-3">
                    Event Type <span className="text-[#C9A961]">*</span>
                  </label>
                  <select
                    name="event_type"
                    required
                    defaultValue=""
                    className="w-full bg-transparent border-b border-[#D8D0C4] focus:border-[#C9A961] outline-none text-[#1A1A1A] font-light text-sm tracking-wide pb-2 transition-colors appearance-none cursor-pointer"
                  >
                    <option value="" disabled className="text-[#C8C0B5]">Select event type</option>
                    {eventTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 3: Date + Location */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#DDD8D0] mt-px">
                <div className="bg-[#FAF8F5] p-6 md:p-8">
                  <label className="block text-[#8A7F74] text-[10px] tracking-[0.25em] uppercase font-light mb-3">
                    Preferred Date
                  </label>
                  <input
                    type="date"
                    name="date"
                    className="w-full bg-transparent border-b border-[#D8D0C4] focus:border-[#C9A961] outline-none text-[#1A1A1A] font-light text-sm tracking-wide pb-2 transition-colors cursor-pointer"
                  />
                </div>
                <div className="bg-[#FAF8F5] p-6 md:p-8">
                  <label className="block text-[#8A7F74] text-[10px] tracking-[0.25em] uppercase font-light mb-3">
                    Location / City
                  </label>
                  <input
                    type="text"
                    name="location"
                    placeholder="London, Paris, Dubai..."
                    className="w-full bg-transparent border-b border-[#D8D0C4] focus:border-[#C9A961] outline-none text-[#1A1A1A] font-light text-sm tracking-wide pb-2 transition-colors placeholder-[#C8C0B5]"
                  />
                </div>
              </div>

              {/* Row 4: Guests + Budget */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#DDD8D0] mt-px">
                <div className="bg-[#FAF8F5] p-6 md:p-8">
                  <label className="block text-[#8A7F74] text-[10px] tracking-[0.25em] uppercase font-light mb-3">
                    Number of Guests
                  </label>
                  <input
                    type="number"
                    name="guests"
                    min="1"
                    placeholder="e.g. 12"
                    className="w-full bg-transparent border-b border-[#D8D0C4] focus:border-[#C9A961] outline-none text-[#1A1A1A] font-light text-sm tracking-wide pb-2 transition-colors placeholder-[#C8C0B5]"
                  />
                </div>
                <div className="bg-[#FAF8F5] p-6 md:p-8">
                  <label className="block text-[#8A7F74] text-[10px] tracking-[0.25em] uppercase font-light mb-3">
                    Budget Range
                  </label>
                  <select
                    name="budget_range"
                    defaultValue=""
                    className="w-full bg-transparent border-b border-[#D8D0C4] focus:border-[#C9A961] outline-none text-[#1A1A1A] font-light text-sm tracking-wide pb-2 transition-colors appearance-none cursor-pointer"
                  >
                    <option value="" disabled>Select a range</option>
                    {budgetRanges.map((range) => (
                      <option key={range} value={range}>{range}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 5: Vision full width */}
              <div className="bg-[#FAF8F5] border border-[#DDD8D0] border-t-0 p-6 md:p-8 mt-px">
                <div className="flex items-end justify-between mb-3">
                  <label className="block text-[#8A7F74] text-[10px] tracking-[0.25em] uppercase font-light">
                    Your Vision <span className="text-[#C9A961]">*</span>
                  </label>
                  <span className={`text-[10px] tracking-wide font-light ${charCount > 450 ? "text-[#C9A961]" : "text-[#C8C0B5]"}`}>
                    {charCount} / 500
                  </span>
                </div>
                <textarea
                  name="vision"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="Describe your ideal experience — the occasion, the atmosphere, any specific details that matter to you. The more you share, the more precisely we can tailor your proposal."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full bg-transparent border-b border-[#D8D0C4] focus:border-[#C9A961] outline-none text-[#1A1A1A] font-light text-sm leading-relaxed tracking-wide pb-2 transition-colors placeholder-[#C8C0B5] resize-none"
                />
              </div>

              {/* Divider */}
              <div className="pt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                <p className="text-[#B0A898] text-xs tracking-[0.15em] font-light leading-relaxed max-w-xs">
                  All submissions are treated with complete discretion. We respond within 48 hours.
                </p>

                <button
                  type="submit"
                  disabled={status === "submitting" || charCount > 500}
                  className="inline-flex items-center gap-3 px-10 py-4 bg-[#1A1A1A] text-[#FAF8F5] text-xs tracking-[0.2em] uppercase font-light hover:bg-[#2C2420] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === "submitting" ? (
                    <>
                      <span className="inline-block w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Request Private Access
                      <i className="ri-arrow-right-line text-sm" />
                    </>
                  )}
                </button>
              </div>

              {status === "error" && (
                <p className="mt-4 text-[#8A7F74] text-xs tracking-wide font-light">
                  Something went wrong. Please try again or contact us directly at hello@tableprive.com
                </p>
              )}
            </form>
          )}
        </div>
      </section>

      {/* Bottom trust strip */}
      <section className="py-14 px-8 md:px-20 border-t border-[#E8E2D9]">
        <div className="max-w-4xl flex flex-col md:flex-row items-start md:items-center gap-10 md:gap-20">
          {[
            { icon: "ri-lock-line", label: "Complete Discretion", sub: "Every inquiry is handled in strict confidence." },
            { icon: "ri-time-line", label: "48-Hour Response", sub: "Our team responds thoughtfully and promptly." },
            { icon: "ri-sparkling-line", label: "Fully Bespoke", sub: "No two experiences are ever alike." },
          ].map((item) => (
            <div key={item.label} className="flex items-start gap-5">
              <div className="w-8 h-8 flex items-center justify-center text-[#C9A961] flex-shrink-0 mt-0.5">
                <i className={`${item.icon} text-xl`} />
              </div>
              <div>
                <p className="text-[#1A1A1A] text-sm font-light tracking-widest uppercase mb-1">{item.label}</p>
                <p className="text-[#8A7F74] text-xs font-light tracking-wide leading-relaxed">{item.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </main>
  );
}
